import clsx from 'clsx';
import { Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';

import { moneyFormatter } from '@utils';
import { InitialCard } from '@components';
import { useSettingsContext } from '@providers';
import { CreditStatus, creditStatusMap } from '@types';
import { ActivePipelineReport, reportsHttp } from '@network';
import cls from './ActivePipeline.module.css';

interface Props {
  range: string;
  setLoader: (num: number, value: boolean) => void;
}

const colors = {
  [CreditStatus.MODERATION]: '#2196F3',
  [CreditStatus.PRE_SCREENING]: '#1D1DB2',
  [CreditStatus.DOC_COLLECTION]: '#3B3BB8',
  [CreditStatus.KYC_SCREENING]: '#007BFF',
  [CreditStatus.POST_SCREENING]: '#00BFFF',
  [CreditStatus.COMPLETED_FILE]: '#204BCE',
  [CreditStatus.BANK_REVISION]: '#009688',
  [CreditStatus.APPROVED]: '#007002',
  [CreditStatus.IN_NEGOTIATIONS]: '#2E8B57',
  [CreditStatus.CONTRACT_IN_PROCESS]: '#008000',
  // [CreditStatus.SIGNED]: '#4CAF50',
};

const creditStatuses = [
  { value: CreditStatus.MODERATION, disableBox: false },
  { value: CreditStatus.PRE_SCREENING },
  { value: CreditStatus.DOC_COLLECTION },
  { value: CreditStatus.KYC_SCREENING },
  { value: CreditStatus.POST_SCREENING },
  { value: CreditStatus.COMPLETED_FILE },
  { value: CreditStatus.BANK_REVISION },
  { value: CreditStatus.APPROVED },
  { value: CreditStatus.IN_NEGOTIATIONS },
  { value: CreditStatus.CONTRACT_IN_PROCESS },
];


export const ActivePipeline = ({ range, setLoader }: Props) => {

  const { admin } = useSettingsContext();
  const [numbers, setNumbers] = useState<ActivePipelineReport>({});
  const [chartValues, setChartValues] = useState<Record<string, number>>({});
  const [showCharts, setShowCharts] = useState<CreditStatus[]>([
    CreditStatus.PRE_SCREENING,
    CreditStatus.DOC_COLLECTION,
    CreditStatus.KYC_SCREENING,
    CreditStatus.POST_SCREENING,
    CreditStatus.COMPLETED_FILE,
    CreditStatus.BANK_REVISION,
    CreditStatus.APPROVED,
    CreditStatus.IN_NEGOTIATIONS,
    CreditStatus.CONTRACT_IN_PROCESS,
  ]);

  const loadNumbers = async () => {
    setLoader(1, true);
    try {
      const nextNumbers = await reportsHttp.activePipeline({ range });
      setNumbers(nextNumbers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(1, false);
    }
  };

  const toggleCharts = (key: CreditStatus) => {
    setShowCharts(state => {
      if (state.includes(key)) {
        return state.filter(item => item !== key);
      }

      return [...state, key];
    });
  };

  useEffect(() => {
    if (admin) {
      loadNumbers();
    }
  }, [range]);

  const creditCharts = creditStatuses.filter(status => showCharts.includes(status.value));

  useEffect(() => {
    const numbersFilteres = {
      ...numbers,
      [CreditStatus.MODERATION]: { count: 0 },
    };
    const values = Object.values(numbersFilteres).map(number => number.count);
    const maxValue = Math.max(...(values.filter(Boolean)));
    const maxPercent = 90;

    setChartValues(state => {
      Object.entries(numbersFilteres).forEach(([key, value]) => {
        state[key] = Math.floor(((value.count || 0) / maxValue) * maxPercent);
      });

      return state;
    });

  }, [numbers]);

  return (
    <InitialCard
      left="Active Pipeline"
      extraPadding
      autoHeight
      mt={22}
      rightMessage="Not affected by time filter"
      rightTooltipColor={'#042E6B'}
      rightIcon={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="#F38336">
          <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 27-3 53t-10 51q-14-16-32.5-27T794-418q3-15 4.5-30.5T800-480q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93q51 0 97.5-15t85.5-42q12 17 29.5 30t37.5 20q-51 41-114.5 64T480-80Zm290-160q-21 0-35.5-14.5T720-290q0-21 14.5-35.5T770-340q21 0 35.5 14.5T820-290q0 21-14.5 35.5T770-240Zm-158-52L440-464v-216h80v184l148 148-56 56Z" />
        </svg>
      }
    >
      <br />
      <div className={cls._}>
        <div className={cls.legend}>
          {creditStatuses.map(status => (
            <div className={cls.row} key={`${status.value}-${range}`}>
              <div className={cls.name}>
                {/* @ts-ignore */}
                {!status.hideBox && (
                  <div
                    className={clsx(
                      cls.box,
                      // @ts-ignore
                      status.disableBox && cls.disabled,
                      !showCharts.includes(status.value) && cls.inactive,
                    )}
                    style={{
                      background: colors[status.value],
                    }}
                    // @ts-ignore
                    onClick={() => !status.disableBox ? toggleCharts(status.value) : null}
                  />
                )}
                {creditStatusMap[status.value]}
              </div>
              <div className={cls.amount}>
                {moneyFormatter.format(numbers[status.value]?.amount || 0)}
              </div>
              <div className={cls.count}>{numbers[status.value]?.count || 0}</div>
            </div>
          ))}
        </div>

        <div className={cls.charts}>
          {creditCharts.map(chart => (
            <Tooltip key={`${chart.value}-${range}`}
              title={`${creditStatusMap[chart.value]}: ${numbers[chart.value]?.count || 0}`}>
              <div
                className={cls.chart}
                style={{
                  background: colors[chart.value],
                  width: chartValues[chart.value] ? `${chartValues[chart.value]}%` : 6,
                }}
              />
            </Tooltip>
          ))}
        </div>
      </div>
    </InitialCard>
  );
};
